import { VehicleTestImageFiles, ServiceDetail } from '@/types/vehicleTest';

/**
 * Interface for added image data structure
 */
export interface AddedImage {
  file: File; // Compressed file for upload (0.7MB)
  originalFile?: File; // Original file before compression (for full-size display in popup)
  imageId?: string; // Temp ID of the image (for matching in create mode)
  section: 'exterior' | 'interior' | 'damage' | 'serviceImages' | 'tires';
  isThumbnail?: boolean; // For exterior images
  tireType?: 'primary' | 'secondary';
  comment?: string; // For damage images
}

/**
 * Builds VehicleTestImageFiles structure from added images and service details
 * This selector transforms the component's internal image state into the format
 * required for the CreateTestRequest
 */
export const buildVehicleTestImageFiles = (
  addedImages: AddedImage[],
  addedServiceDetails: ServiceDetail[]
): VehicleTestImageFiles => {
  const imageFiles: VehicleTestImageFiles = {};

  // Group added images by section
  const exteriorFiles: { file: File; isThumbnail?: boolean }[] = [];
  const interiorFiles: { file: File }[] = [];
  const damageFiles: { file: File; comment?: string }[] = [];
  const serviceImageFiles: { file: File }[] = [];
  const tireFiles: {
    primary: { file: File }[];
    secondary: { file: File }[];
  } = {
    primary: [],
    secondary: [],
  };

  // Map added images to their sections
  addedImages.forEach((img) => {
    if (img.section === 'exterior') {
      exteriorFiles.push({ file: img.file, isThumbnail: img.isThumbnail });
    } else if (img.section === 'interior') {
      interiorFiles.push({ file: img.file });
    } else if (img.section === 'damage') {
      // Comment is stored directly in addedImages for create mode
      damageFiles.push({
        file: img.file,
        comment: img.comment || '',
      });
    } else if (img.section === 'serviceImages') {
      serviceImageFiles.push({ file: img.file });
    } else if (img.section === 'tires') {
      const tireType = img.tireType || 'primary';
      tireFiles[tireType].push({ file: img.file });
    }
  });

  // Only include sections that have files
  if (exteriorFiles.length > 0) imageFiles.exterior = exteriorFiles;
  if (interiorFiles.length > 0) imageFiles.interior = interiorFiles;
  if (damageFiles.length > 0) imageFiles.damage = damageFiles;
  if (serviceImageFiles.length > 0) imageFiles.serviceImages = serviceImageFiles;
  
  // Handle tires structure
  if (tireFiles.primary.length > 0 || tireFiles.secondary.length > 0) {
    imageFiles.tires = {};
    if (tireFiles.primary.length > 0) imageFiles.tires.primary = tireFiles.primary;
    if (tireFiles.secondary.length > 0) imageFiles.tires.secondary = tireFiles.secondary;
  }

  // Include service details (only newly added in create mode)
  if (addedServiceDetails.length > 0) {
    imageFiles.serviceDetails = { serviceDetails: addedServiceDetails };
  }

  return imageFiles;
};

